export const paths = {
  auth: {
    login: '/login',
    signUp: '/sign_up',
    forgotPassword: '/forgot_password',
    resetPassword: '/reset_password',
    changePassword: '/change_password',
    emailExists: '/email_exists',
    setProfile: '/set_profile/:userId',
  },
  onboarding: {
    index: '/onboarding',
    step: '/onboarding/:step',
    welcome: '/welcome',
    templates: '/forms/initial_templates',
  },
  home: {
    index: '/',
    home: 'home',
  },
  forms: {
    index: '/forms',
    builder: 'builder/:formId',
    answer: ':formId/answers/:answerId',
    editAnswer: ':formId/answers/:answerId/edit',
    newAnswer: ':formId/answers/new',
    importFolders: 'import_folders',
    aiFormBuilder: 'ai_form_builder',
    oldAnswerDetail: '/form_detail/:answerId',
    pdfEditor: '/edit_pdf',
    initialTemplates: 'initial_templates',
    publishForm: 'builder/:formId/publish_form',
  },
  integrations: {
    index: '/integrations',
    spreadsheets: '/integrations/spreadsheets',
    spreadsheetShow: '/integrations/spreadsheets/:webHookId',
    spreadsheetLogs: '/integrations/spreadsheets/:webHookId/logs',
    spreadsheetEdit: '/integrations/spreadsheets/:webHookId/edit',
    webhooks: '/integrations/webhooks',
    webhookShow: '/integrations/webhooks/:webHookId',
    webhookLogs: '/integrations/webhooks/:webHookId/logs',
    webhookNew: '/integrations/webhooks/new',
    webhookEdit: '/integrations/webhooks/:webHookId/edit',
  },
  taskAssigner: {
    index: '/task_assigner',
    new: 'new',
    edit: ':taskId/edit',
    import: 'import',
    history: ':taskId/history',
    groupDetail: 'groups/:groupTaskAssignId/detail',
    groupEdit: 'groups/:groupTaskAssignId/edit',
  },
  plannings: {
    index: '/plannings',
    new: 'new',
    detail: ':planningId',
    edit: ':planningId/edit',
    types: 'types',
    typeNew: 'types/new',
    typeEdit: 'types/:typeId/edit',
    /** Printable Cronograma page (DS-3778, U10): one page/group of items,
     *  rendered in ?pdf_mode=true and navigated by the backend's PDF export
     *  fan-out via /web_redirect, mirroring the form-answer PDF pattern. */
    timelinePrint: 'timeline/print',
  },
  findings: {
    index: '/issues',
    settings: 'settings',
    type: 'types/:typeId',
  },
  signature: {
    index: '/signature',
    verifyUser: 'verify_user',
    activationRequest: 'activation-request',
  },
  lists: {
    index: '/lists',
    newLocation: '/new_location',
    create: '/lists/create',
    edit: '/lists/edit/:id',
    detail: '/lists/:id',
  },
  auditLogs: {
    index: '/audit_logs',
    imports: '/audit_logs/imports',
    taskImports: '/audit_logs/task_imports',
    taskImportsDetail: '/audit_logs/task_imports/:auditId',
  },
  assets: {
    types: '/asset_types',
    new: '/asset_types/new',
    edit: '/asset_types/:assetTypeId/edit',
    assets: '/asset_types/:assetTypeId/assets',
    newAsset: '/asset_types/assets/new',
    editAsset: '/asset_types/:assetTypeId/assets/:assetId/edit',
    showAsset: '/asset_types/:assetTypeId/assets/:assetId',
    meterMeasurementsAsset: '/asset_types/:assetTypeId/assets/:assetId/meters/:meterId',
    metersAsset: '/asset_types/:assetTypeId/assets/:assetId/meters',
    sensorsAsset: '/asset_types/:assetTypeId/assets/:assetId/sensors',
  },
  meters: {
    index: '/meters',
  },
  charts: {
    index: '/charts',
    chartModules: '/chart_modules',
  },
  map: {
    index: '/map',
  },
  sensors: {
    index: '/sensors',
    map: '/sensors/map',
    list: '/sensors/list',
    showSensor: '/sensors/list/:sensorId',
    inbox: '/sensors/inbox',
    connection: '/sensors/connection',
  },
  training: {
    index: '/training',
  },
  changeLog: {
    index: '/changelog',
  },
  account: {
    index: '/account',
    accountSecurity: '/account/security',
  },
  whatsapp: {
    index: '/whatsapp',
  },
  users: {
    index: '/users',
    user: ':userId',
    groupsImport: 'groups_import',
  },
  billing: {
    index: '/billing',
    modifySubscription: '/billing/modify',
  },
  public: {
    answer: '/public/forms/:formId/answers/:answerId',
    expiredLink: '/public/answer_view_expired_link',
    finding: '/public/findings/:findingId',
    documentValidation: '/document_validation/:hash',
    webforms: '/webforms',
    webformsPublic: '/public/webforms',
  },
  locations: {
    index: '/locations',
    migration: '/locations/migration',
    edit: '/locations/edit/:id',
    create: '/locations/create/:parentId',
    createRoot: '/locations/create',
    view: '/locations/view/:id',
  },
  templates: {
    index: '/templates',
    use: '/templates/:shareId/use',
  },
  mediaGallery: {
    index: '/media_gallery',
  },
  askDataScope: {
    index: '/ask_datascope',
  },
  dashboards: {
    index: '/dashboards',
    detail: (id: number) => `/dashboards/${id}` as const,
  },
  opsCenter: {
    index: '/ops_center',
    featureFlags: '/ops_center/feature_flags',
    supportCommands: '/ops_center/support_commands',
    alerts: '/ops_center/alerts',
    customerOps: '/ops_center/customer_ops',
    supportAccess: '/ops_center/support_access',
    dsAdmin: '/ops_center/ds_admin',
  },
  // Herramienta interna de consulta de estado de cuenta. Standalone a
  // propósito: no cuelga de ops_center porque su gate es otro.
  accountStatus: {
    index: '/account-status',
  },
};

export const redirectTo = (url: string, absolute = false) => {
  window.top.location.href = absolute ? url : `${process.env.REACT_APP_BACKEND_ENDPOINT}/${url}`;
};

export const openInNewTab = (url: string, absolute = false) => {
  window.open(absolute ? url : `${process.env.REACT_APP_BACKEND_ENDPOINT}/${url}`, '_blank');
};

export const openFrontendRouteInNewTab = (path: string) => {
  const currentOrigin = window.location.origin;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  window.open(`${currentOrigin}${cleanPath}`, '_blank');
};

/**
 * Builds a URL with query parameters
 * @param basePath - The base path for the URL
 * @param params - Object with key-value pairs to append as query params
 * @param preserveTracking - Whether to preserve tracking parameters from searchParams
 * @param searchParams - URLSearchParams object containing current URL parameters
 * @returns Complete URL with query string
 */
export const buildReturnUrl = (
  basePath: string,
  params: Record<string, string | number | null | undefined>,
  preserveTracking = false,
  searchParams?: URLSearchParams,
): string => {
  const urlParams = new URLSearchParams();

  // Add provided params
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      urlParams.append(key, String(value));
    }
  });

  // Optionally preserve tracking params
  if (preserveTracking && searchParams) {
    const trackingParams = ['__hstc', '__hssc', '__hsfp', '_gl'];
    trackingParams.forEach((param) => {
      const value = searchParams.get(param);
      if (value) urlParams.append(param, value);
    });
  }

  const queryString = urlParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};
